import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";

const MEMBER_VERIFIED_ANSWERS_FILE = "SupplimentalFAQ.md";

const buildInitialEntry = entryRecord => {
  const record = entryRecord || {};

  return {
    id: record.id ?? null,
    topic: record.topic ?? "",
    clarification: record.clarification ?? "",
    sourceFile: record.sourceFile ?? MEMBER_VERIFIED_ANSWERS_FILE,
    lineNumber: record.lineNumber ?? "",
  };
};

const BotpressMemberVerifiedAnswersDialog = ({
  open,
  entryRecord,
  dialogTitle = "Frequently Asked Question",
  okMessage = "Ok",
  cancelMessage = "Cancel",
  deleteMessage = "Delete",
  maxWidth = "sm",
  fullWidth = true,
  onAccept,
  onDelete,
  onClose,
}) => {
  const theme = useTheme();

  // The dialog is mounted fresh each time it opens, so seeding state once here
  // is correct and avoids an effect that could reset the fields mid-typing.
  const [editedEntry, setEditedEntry] = useState(() => buildInitialEntry(entryRecord));

  const updateField = (fieldName, value) => {
    setEditedEntry(previousEntry => ({
      ...previousEntry,
      [fieldName]: value,
    }));
  };

  const formHasError = !editedEntry.topic.trim() || !editedEntry.clarification.trim();

  const onAcceptEntry = () => {
    if (formHasError) {
      return;
    }

    onAccept({
      ...editedEntry,
      topic: editedEntry.topic.trim(),
      clarification: editedEntry.clarification.trim(),
      sourceFile: editedEntry.sourceFile || MEMBER_VERIFIED_ANSWERS_FILE,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onClose}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Topic or Question"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"mvaTopic"}
              value={editedEntry.topic}
              onChange={event => updateField("topic", event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Clarification"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"mvaClarification"}
              rowCount={8}
              multiline={true}
              value={editedEntry.clarification}
              onChange={event => updateField("clarification", event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {editedEntry.id != null && onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => onDelete(editedEntry)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onClose}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptEntry}
            color="secondary"
            variant="contained"
            disabled={formHasError}
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BotpressMemberVerifiedAnswersDialog;
